/**
 * 79 preguntas sugeridas para el asistente (pill «Sugiéreme algo»).
 * `filter`: ventas | stock | socios | analisis.
 * `prompt`: texto listo para enviar al chat (alineado con tools del backend).
 */

export const ASSISTANT_SUGGESTION_FILTERS = [
  { id: 'todas', label: 'Todas' },
  { id: 'ventas', label: 'Ventas' },
  { id: 'stock', label: 'Stock' },
  { id: 'socios', label: 'Socios' },
  { id: 'analisis', label: 'Análisis' },
];

/** Clase CSS `wa-asistente-suggestion-area--*` por área (filtros y tags). */
export const ASSISTANT_SUGGESTION_TAG_STYLES = {
  ventas: 'wa-asistente-suggestion-area--ventas',
  stock: 'wa-asistente-suggestion-area--stock',
  socios: 'wa-asistente-suggestion-area--socios',
  analisis: 'wa-asistente-suggestion-area--analisis',
  multi: 'wa-asistente-suggestion-area--multi',
  estrategia: 'wa-asistente-suggestion-area--estrategia',
  operacion: 'wa-asistente-suggestion-area--operacion',
  bistro: 'wa-asistente-suggestion-area--bistro',
};

const AREA_STYLE_BY_TAG = {
  Ventas: 'ventas',
  Stock: 'stock',
  Socios: 'socios',
  Multi: 'multi',
  Estrategia: 'estrategia',
  Operación: 'operacion',
  Bistro: 'bistro',
};

/** @type {{ id: string; prompt: string; title: string; subtitle: string; filter: string; tag: string; icon: 'trending'|'alert'|'users'|'chart'|'lightbulb'|'layout'; venueTypes?: string[] }[]} */
export const ASSISTANT_SUGGESTED_QUESTIONS = [
  // —— Ventas y facturación (01–10) ——
  {
    id: '01',
    prompt: 'Resumen de facturación de hoy: importe total y número de ventas.',
    title: 'Facturación de hoy',
    subtitle: 'Importe y transacciones del día en curso.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '02',
    prompt: 'Facturación de los últimos 7 días: importe acumulado y número de ventas.',
    title: 'Ventas de la última semana',
    subtitle: 'Total facturado y recuento de tickets.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '03',
    prompt:
      'Compara las ventas de los últimos 14 días: esta semana frente a la anterior, con cifras.',
    title: 'Esta semana vs la anterior',
    subtitle: 'Tendencia quincenal con datos reales.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '04',
    prompt: 'En los últimos 7 días, ¿qué día tuvo más ventas y cuál el menos? Indica importes.',
    title: 'Mejor y peor día de la semana',
    subtitle: 'Día pico y día flojo.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '05',
    prompt: 'Tabla de ventas por día de los últimos 30 días.',
    title: 'Ventas diarias del último mes',
    subtitle: 'Desglose día a día en tabla.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'chart',
  },
  {
    id: '06',
    prompt: 'Últimas 5 transacciones con importe, socio y productos.',
    title: 'Últimas 5 transacciones',
    subtitle: 'Detalle reciente del TPV.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '07',
    prompt: 'Las 10 ventas más recientes: socio, importe y productos de cada una.',
    title: '10 ventas más recientes',
    subtitle: 'Tickets recientes con detalle.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '08',
    prompt: 'En los últimos 7 días, ¿hubo algún día sin ventas registradas?',
    title: 'Días sin ventas esta semana',
    subtitle: 'Huecos en la actividad comercial.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '09',
    prompt:
      'Resumen en viñetas de los últimos 7 días: facturación, número de ventas y ticket medio.',
    title: 'Resumen semanal en viñetas',
    subtitle: 'KPIs clave de la semana.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '10',
    prompt:
      'Ventas por día de la última semana en tabla y, si procede, gráfico de barras.',
    title: 'Semana en tabla y gráfico',
    subtitle: 'Visualización de la última semana.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'chart',
  },
  // —— Inventario y stock (11–20) ——
  {
    id: '11',
    prompt: 'Productos con stock por debajo de 10 unidades.',
    title: 'Stock bajo (menos de 10 uds.)',
    subtitle: 'Referencias a reponer.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '12',
    prompt: 'Stock crítico (menos de 5 unidades), agrupado por categoría si es posible.',
    title: 'Stock crítico por categoría',
    subtitle: 'Prioridad máxima en almacén.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '13',
    prompt: 'Número de productos en catálogo y valor estimado total del inventario.',
    title: 'Catálogo y valor de inventario',
    subtitle: 'Referencias y valor a precio de catálogo.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '14',
    prompt: 'Qué productos reponer con urgencia según stock bajo y ritmo de venta reciente.',
    title: 'Reposición urgente por rotación',
    subtitle: 'Stock bajo cruzado con demanda.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '15',
    prompt: 'Cinco productos con más stock y cinco con menos unidades.',
    title: 'Top y bottom de inventario',
    subtitle: 'Extremos del stock actual.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '16',
    prompt: 'Qué productos se agotarán pronto según el ritmo de venta de las últimas semanas.',
    title: 'Riesgo de rotura de stock',
    subtitle: 'Predicción por consumo reciente.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '17',
    prompt: 'Lista de reposición urgente esta semana: stock bajo y predicción de agotamiento.',
    title: 'Pedido urgente de la semana',
    subtitle: 'Stock crítico y predicción combinados.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '18',
    prompt: 'Resumen ejecutivo del inventario para reunión con el proveedor.',
    title: 'Inventario para el proveedor',
    subtitle: 'Briefing listo para negociar.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  {
    id: '19',
    prompt: 'Tres referencias prioritarias para el pedido al proveedor antes del fin de semana.',
    title: 'Top 3 para el fin de semana',
    subtitle: 'Predicción y stock crítico.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'lightbulb',
  },
  {
    id: '20',
    prompt: 'Productos con stock 0 en este momento; lista de nombres.',
    title: 'Referencias agotadas',
    subtitle: 'Sin unidades disponibles.',
    filter: 'stock',
    tag: 'Stock',
    icon: 'alert',
  },
  // —— Socios: altas, visitas y saldos (21–32) ——
  {
    id: '21',
    prompt: 'Número total de socios activos en el club.',
    title: 'Total de socios',
    subtitle: 'Recuento global de miembros.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '22',
    prompt: 'Socios dados de alta esta semana: cantidad, nombres y fechas.',
    title: 'Altas de esta semana',
    subtitle: 'Nuevos miembros recientes.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '23',
    prompt: 'Altas de socios en los últimos 30 días, con los más recientes primero.',
    title: 'Altas del último mes',
    subtitle: 'Inscripciones, no visitas.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '24',
    prompt: 'Cuántos socios distintos visitaron el club en los últimos 7 días.',
    title: 'Visitas de la semana',
    subtitle: 'Afluencia en puerta, no altas.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '25',
    prompt: 'Socios únicos que entraron al club en los últimos 30 días.',
    title: 'Afluencia del último mes',
    subtitle: 'Visitantes únicos en 30 días.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '26',
    prompt: 'Los 5 socios con mayor saldo en cuenta.',
    title: 'Top 5 por saldo',
    subtitle: 'Saldos más altos del club.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '27',
    prompt: 'Buscar socios con apellido García: nombre, email y saldo.',
    title: 'Búsqueda por apellido',
    subtitle: 'Localizar miembros por nombre.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '28',
    prompt: 'Diez socios dados de alta más recientemente, con nombre y email.',
    title: '10 altas más recientes',
    subtitle: 'Últimos miembros registrados.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '29',
    prompt: 'Socios VIP por gasto y socios con más de 30 días sin visitar.',
    title: 'VIP e inactivos',
    subtitle: 'Segmentación para retención.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '30',
    prompt: 'Lista breve de socios inactivos (+30 días sin visita) para campaña de reactivación.',
    title: 'Inactivos para reactivar',
    subtitle: 'Base para retención.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '31',
    prompt: 'Últimos check-ins en la puerta: nombre y hora.',
    title: 'Entradas más recientes',
    subtitle: 'Actividad en tiempo real.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '32',
    prompt: 'Últimos 10 registros de entrada con nombre y hora.',
    title: '10 últimas entradas',
    subtitle: 'Control de aforo.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  // —— Personal y operación (33–36) ——
  {
    id: '33',
    prompt: 'Equipo del club (owners y staff) con rol y email.',
    title: 'Equipo del venue',
    subtitle: 'Personal registrado.',
    filter: 'analisis',
    tag: 'Operación',
    icon: 'layout',
  },
  {
    id: '34',
    prompt: 'Número de personas de staff y owners registradas.',
    title: 'Tamaño del equipo',
    subtitle: 'Recuento operativo.',
    filter: 'analisis',
    tag: 'Operación',
    icon: 'layout',
  },
  {
    id: '35',
    prompt: 'Check-ins de hoy en el club: nombre y hora de los más recientes.',
    title: 'Actividad en puerta hoy',
    subtitle: 'Entradas del día.',
    filter: 'analisis',
    tag: 'Operación',
    icon: 'layout',
  },
  {
    id: '36',
    prompt: 'Cruce de entradas recientes y ventas de hoy: ¿hay movimiento en el local?',
    title: 'Pulso del local hoy',
    subtitle: 'Entradas y ventas combinadas.',
    filter: 'analisis',
    tag: 'Operación',
    icon: 'layout',
  },
  // —— Análisis cruzado y estrategia (37–48) ——
  {
    id: '37',
    prompt:
      'Dashboard en viñetas: ventas 7 días, stock crítico, socios nuevos y alertas de predicción.',
    title: 'Dashboard operativo',
    subtitle: 'Visión 360° en un prompt.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '38',
    prompt: 'Tres acciones operativas prioritarias para esta semana, basadas en los datos del club.',
    title: '3 acciones de la semana',
    subtitle: 'Recomendaciones accionables.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '39',
    prompt: 'Pulso del mes: ventas recientes, afluencia de socios y altas nuevas.',
    title: 'Resumen del mes',
    subtitle: 'Visión global del periodo.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '40',
    prompt:
      'Si las ventas de esta semana van peor que la anterior, hipótesis cruzando stock y afluencia.',
    title: 'Diagnóstico de caída de ventas',
    subtitle: 'Ventas, stock y visitas.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '41',
    prompt: 'Prioriza en tres puntos: reponer inventario, retener inactivos o fidelizar VIP.',
    title: 'Reponer, retener o fidelizar',
    subtitle: 'Decisión operativa con datos.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '42',
    prompt:
      'Briefing de fin de semana: ventas recientes, entradas de socios y productos en riesgo de agotarse.',
    title: 'Briefing de fin de semana',
    subtitle: 'Lo esencial antes de abrir.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '43',
    prompt:
      'En 7 días: cuántas altas nuevas y cuántas visitas distintas; explica la diferencia.',
    title: 'Altas vs visitas (7 días)',
    subtitle: 'Inscripciones y puerta.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'lightbulb',
  },
  {
    id: '44',
    prompt: 'Ticket medio de los últimos 7 días.',
    title: 'Ticket medio semanal',
    subtitle: 'Importe medio por venta.',
    filter: 'analisis',
    tag: 'Ventas',
    icon: 'chart',
  },
  {
    id: '45',
    prompt: 'Resumen ejecutivo de 8 líneas para WhatsApp al socio fundador.',
    title: 'Resumen para WhatsApp',
    subtitle: 'Cifras clave en formato breve.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '46',
    prompt: 'Según ventas recientes y predicción de stock, qué producto está forzando roturas.',
    title: 'Producto que fuerza roturas',
    subtitle: 'Ventas y predicción cruzadas.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'lightbulb',
  },
  {
    id: '47',
    prompt: 'Tabla de ventas por día de la última semana y gráfico de barras si aplica.',
    title: 'Semana: tabla y gráfico',
    subtitle: 'Doble formato visual.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'chart',
  },
  {
    id: '48',
    prompt: 'Gráfico circular con la cuota de ventas de los 3 mejores días de la última semana.',
    title: 'Top 3 días en gráfico',
    subtitle: 'Comparativa visual.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'chart',
  },
  // —— Bistro (49–50; venues tipo bistro) ——
  {
    id: '49',
    prompt: 'Registrar pedido: dos cervezas y una agua con gas.',
    title: 'Pedido de ejemplo (bistro)',
    subtitle: 'Cervezas y agua con gas.',
    filter: 'analisis',
    tag: 'Bistro',
    icon: 'lightbulb',
    venueTypes: ['bistro'],
  },
  {
    id: '50',
    prompt: 'Opciones de café en carta; al confirmar el nombre, crear el pedido.',
    title: 'Buscar café y pedir',
    subtitle: 'Catálogo y pedido en bistro.',
    filter: 'analisis',
    tag: 'Bistro',
    icon: 'lightbulb',
    venueTypes: ['bistro'],
  },
  // —— Nuevas tools (51–60) ——
  {
    id: '51',
    prompt: 'Comparación de ventas: mes actual frente al mes anterior.',
    title: 'Mes actual vs anterior',
    subtitle: 'Tendencia mensual de facturación.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'chart',
  },
  {
    id: '52',
    prompt: 'Top 10 productos más vendidos del último mes por cantidad e importe.',
    title: 'Top 10 del mes',
    subtitle: 'Ranking de ventas reales.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'trending',
  },
  {
    id: '53',
    prompt: 'Socios con cuota que caduca en los próximos 30 días, con fecha de vencimiento.',
    title: 'Cuotas por caducar',
    subtitle: 'Renovaciones próximas.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '54',
    prompt: 'Quién está dentro del club en este momento (aforo actual).',
    title: 'Aforo en sala ahora',
    subtitle: 'Check-ins activos.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '55',
    prompt: 'Resumen demográfico de socios: edades, ciudades y tarifas (género solo si hay dato).',
    title: 'Demografía de socios',
    subtitle: 'Perfil del club.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '56',
    prompt: 'Pedidos pendientes o en cocina en este momento.',
    title: 'Cola de pedidos (bistro)',
    subtitle: 'Estado de cocina.',
    filter: 'analisis',
    tag: 'Bistro',
    icon: 'lightbulb',
    venueTypes: ['bistro'],
  },
  {
    id: '57',
    prompt: 'Eventos programados en los próximos 14 días.',
    title: 'Agenda de 2 semanas',
    subtitle: 'Calendario del venue.',
    filter: 'analisis',
    tag: 'Operación',
    icon: 'layout',
  },
  {
    id: '58',
    prompt: 'Preinscripciones pendientes: cantidad y listado.',
    title: 'Preinscripciones QR',
    subtitle: 'Solicitudes sin revisar.',
    filter: 'socios',
    tag: 'Socios',
    icon: 'users',
  },
  {
    id: '59',
    prompt: 'Ficha del venue: tipo, plan, límite de socios y total actual.',
    title: 'Ficha del establecimiento',
    subtitle: 'Datos del club activo.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '60',
    prompt: 'Comparativa de ventas: esta semana frente a la semana anterior.',
    title: 'Semana actual vs anterior',
    subtitle: 'Tendencia de facturación.',
    filter: 'ventas',
    tag: 'Ventas',
    icon: 'chart',
  },
  // —— Estrategia, marketing y retención (61–78) ——
  {
    id: '61',
    prompt:
      'Briefing de apertura de hoy en 6 viñetas: ventas recientes, aforo, stock crítico, eventos del día y una alerta prioritaria.',
    title: 'Briefing de apertura',
    subtitle: 'Vista rápida para arrancar el día.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '62',
    prompt:
      'Plan de reactivación para socios inactivos (+30 días sin visita): segmento, mensaje borrador y 3 acciones concretas.',
    title: 'Reactivar inactivos',
    subtitle: 'Retención con datos reales.',
    filter: 'socios',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '63',
    prompt:
      'Cuatro ideas de publicación para redes esta semana, basadas en ventas, productos estrella y eventos del calendario.',
    title: '4 posts para redes',
    subtitle: 'Calendario editorial con datos.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '64',
    prompt:
      'Calendario de acciones para las próximas 2 semanas: eventos programados, picos de afluencia y tareas de stock o socios.',
    title: 'Plan de 2 semanas',
    subtitle: 'Operación + comunicación.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '65',
    prompt:
      'Checklist de viernes para el fin de semana: reposición urgente, personal, promos sugeridas y riesgos de stock.',
    title: 'Checklist de fin de semana',
    subtitle: 'Preparar viernes con datos.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'layout',
  },
  {
    id: '66',
    prompt:
      'Socios con cuota que caduca en 30 días: cuántos hay, perfil resumido y guion breve de contacto (borrador).',
    title: 'Renovación de cuotas',
    subtitle: 'Anticipar bajas de socios.',
    filter: 'socios',
    tag: 'Estrategia',
    icon: 'users',
  },
  {
    id: '67',
    prompt:
      'Plan para fidelizar a los socios VIP por gasto: cómo reconocerlos, qué ofrecer y qué evitar, con datos actuales.',
    title: 'Fidelizar VIP',
    subtitle: 'Retención del top de gasto.',
    filter: 'socios',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '68',
    prompt:
      'Tres ideas de actividades en sala alineadas con el próximo evento del calendario y la afluencia habitual.',
    title: 'Actividades para el próximo evento',
    subtitle: 'Brainstorm con contexto real.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '69',
    prompt:
      'Propuesta de promoción basada en productos más vendidos y categorías con peor rendimiento del último mes.',
    title: 'Idea de promoción',
    subtitle: 'Mix de catálogo y ventas.',
    filter: 'ventas',
    tag: 'Estrategia',
    icon: 'trending',
  },
  {
    id: '70',
    prompt:
      'Si las ventas van peor que el periodo anterior, diagnóstico cruzando facturación, visitas y stock; hipótesis y acciones.',
    title: 'Diagnóstico de caída',
    subtitle: 'Ventas, afluencia e inventario.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '71',
    prompt:
      'Borradores cortos de mensaje WhatsApp para renovar cuota y para invitar a recargar saldo a socios con saldo bajo.',
    title: 'Copys WhatsApp (borrador)',
    subtitle: 'Renovación y recarga.',
    filter: 'socios',
    tag: 'Estrategia',
    icon: 'users',
  },
  {
    id: '72',
    prompt:
      'Horas punta de entradas del último mes y recomendación de refuerzo de personal o apertura en esas franjas.',
    title: 'Horas punta y personal',
    subtitle: 'Afluencia y operación.',
    filter: 'analisis',
    tag: 'Operación',
    icon: 'layout',
  },
  {
    id: '73',
    prompt:
      'Estado del embudo de preinscripciones: pendientes, volumen y 3 pasos para convertirlas en altas.',
    title: 'Preinscripciones pendientes',
    subtitle: 'Captación y conversión.',
    filter: 'socios',
    tag: 'Estrategia',
    icon: 'users',
  },
  {
    id: '74',
    prompt:
      'Plan de comunicación para el fin de semana: mensaje principal, público objetivo y canal sugerido, con datos del club.',
    title: 'Comunicación de fin de semana',
    subtitle: 'Mensaje y audiencia.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '75',
    prompt:
      'Oportunidades de upsell: ticket medio reciente, productos estrella y dos acciones para subir el ticket sin inventar precios.',
    title: 'Subir el ticket medio',
    subtitle: 'Upsell con datos.',
    filter: 'ventas',
    tag: 'Estrategia',
    icon: 'trending',
  },
  {
    id: '76',
    prompt:
      'Resumen de rentabilidad y caja del último mes en lenguaje claro para el owner: qué va bien y qué vigilar.',
    title: 'Salud financiera del mes',
    subtitle: 'Rentabilidad y caja.',
    filter: 'analisis',
    tag: 'Multi',
    icon: 'chart',
    venueTypes: ['cannabis_club', 'social_club', 'cbd_shop'],
  },
  {
    id: '77',
    prompt:
      'Tres acciones operativas prioritarias para esta semana, ordenadas por impacto, con datos de ventas, socios y stock.',
    title: 'Top 3 acciones de la semana',
    subtitle: 'Priorización tipo consultor.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '78',
    prompt:
      'Cinco ideas de actividades de comunidad para el club (taller, networking, jornada temática), alineadas con demografía y afluencia.',
    title: 'Brainstorm de actividades',
    subtitle: 'Comunidad y experiencia.',
    filter: 'analisis',
    tag: 'Estrategia',
    icon: 'lightbulb',
  },
  {
    id: '79',
    prompt:
      'Sugerencias de platos o combos para empujar en carta según lo más vendido y el stock disponible.',
    title: 'Promo de carta (bistro)',
    subtitle: 'Menú y rotación.',
    filter: 'analisis',
    tag: 'Bistro',
    icon: 'lightbulb',
    venueTypes: ['bistro'],
  },
];

const FILTER_AREA_LABEL = Object.fromEntries(
  ASSISTANT_SUGGESTION_FILTERS.filter((f) => f.id !== 'todas').map((f) => [f.id, f.label])
);

/** Etiqueta de área (Ventas, Stock, Socios, Análisis, o `tag` si es más específica: Bistro, Operación…). */
export function getSuggestionAreaLabel(suggestion) {
  if (!suggestion) return '';
  if (suggestion.tag) return suggestion.tag;
  return FILTER_AREA_LABEL[suggestion.filter] || '';
}

/** Clave de estilo de color para el badge de área. */
export function getSuggestionAreaStyleKey(suggestion) {
  if (!suggestion) return 'analisis';
  if (suggestion.tag && AREA_STYLE_BY_TAG[suggestion.tag]) {
    return AREA_STYLE_BY_TAG[suggestion.tag];
  }
  if (ASSISTANT_SUGGESTION_TAG_STYLES[suggestion.filter]) return suggestion.filter;
  return 'analisis';
}

export function getSuggestionAreaBadgeClass(suggestion) {
  const key = getSuggestionAreaStyleKey(suggestion);
  const variant = ASSISTANT_SUGGESTION_TAG_STYLES[key] || ASSISTANT_SUGGESTION_TAG_STYLES.analisis;
  return `wa-asistente-suggestion-area ${variant}`;
}

/** Título en UI: área (badge) + cuerpo sin prefijo entre corchetes. */
export function parseSuggestionTitleParts(suggestion) {
  const area = getSuggestionAreaLabel(suggestion);
  const body = String(suggestion?.title || suggestion?.prompt || '').trim();
  return { area, body };
}

/** Prefija el texto con el área, p. ej. "[Stock] ¿Qué productos…". Evita duplicar si ya lleva prefijo. */
export function formatSuggestionWithArea(text, suggestion) {
  const area = getSuggestionAreaLabel(suggestion);
  const body = String(text || '').trim();
  if (!area || !body) return body;
  const bracket = `[${area}]`;
  if (body.startsWith(`${bracket} `) || body.startsWith(`${bracket}`)) return body;
  if (body.startsWith(`${area}:`) || body.startsWith(`${area} ·`)) return body;
  return `${bracket} ${body}`;
}

export function formatSuggestionTitle(suggestion) {
  return formatSuggestionWithArea(suggestion?.title || suggestion?.prompt || '', suggestion);
}

export function formatSuggestionPrompt(suggestion) {
  return formatSuggestionWithArea(suggestion?.prompt || '', suggestion);
}

/** Tipos de venue con flujo de club (no restaurante). */
export const ASSISTANT_CLUB_VENUE_TYPES = ['cannabis_club', 'social_club', 'cbd_shop'];

export const ASSISTANT_BISTRO_VENUE_TYPES = ['bistro'];

function normalizeAssistantVenueType(venueType) {
  const vt = String(venueType || '')
    .trim()
    .toLowerCase();
  if (ASSISTANT_BISTRO_VENUE_TYPES.includes(vt)) return 'bistro';
  if (ASSISTANT_CLUB_VENUE_TYPES.includes(vt)) return vt;
  return vt || null;
}

/**
 * Preguntas por tipo de venue.
 * - Sin `venueTypes` → solo clubs (cannabis_club, social_club, cbd_shop), nunca bistro.
 * - Con `venueTypes` → solo si el venue actual está en la lista.
 */
export function assistantSuggestionsForVenue(venueType) {
  const vt = normalizeAssistantVenueType(venueType);
  return ASSISTANT_SUGGESTED_QUESTIONS.filter((q) => {
    const allowed = q.venueTypes?.length ? q.venueTypes : ASSISTANT_CLUB_VENUE_TYPES;
    if (!vt) {
      return !q.venueTypes?.includes('bistro');
    }
    return allowed.includes(vt);
  });
}

export function filterAssistantSuggestions(list, filterId) {
  if (!filterId || filterId === 'todas') return list;
  return list.filter((q) => q.filter === filterId);
}
